import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { BaseService } from '../../core';
import { GamificacaoResponse, HistoricoPontuacaoResponse } from 'app/shared/models/ei-models';

/**
 * Identificadores válidos de menu para tracking de gamificação
 * 
 * Plano 1 (básico): home, trilhas, aulas-ao-vivo, ia, ranking, feedbacks, comunicados, meu-perfil
 * Plano 2/3 (completo): todos acima + conquistas, newsletter
 * 
 * NOTA: A badge "Pronto para o Mercado" só é atribuída para plano 2+
 */
export type MenuIdentificador = 
  // Menus disponíveis para todos os planos
  | 'home' 
  | 'trilhas' 
  | 'aulas-ao-vivo'
  | 'ranking' 
  | 'feedbacks' 
  | 'comunicados' 
  | 'meu-perfil' 
  | 'ia'
  // Menus exclusivos plano 2+
  | 'conquistas' 
  | 'newsletter';

@Injectable({ providedIn: 'root' })
export class GamificacaoService extends BaseService {
  private controllerEndpoint = `${environment.urlEdducaImobApi}EdducaImobCore/Gamificacao/`;

  constructor(private http: HttpClient) {
    super();
  }

  /**
   * Registra o primeiro login do dia e retorna pontos ganhos
   * Chamar após login bem-sucedido
   */
  registrarLogin(usuarioId: number): Observable<GamificacaoResponse> {
    return this.http.post<GamificacaoResponse>(
      `${this.controllerEndpoint}RegistrarLogin/${usuarioId}`,
      {},
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Registra participação em aula ao vivo
   * Chamar quando o usuário entrar em uma live
   */
  registrarParticipacaoLive(usuarioId: number, aulaId: number): Observable<GamificacaoResponse> {
    return this.http.post<GamificacaoResponse>(
      `${this.controllerEndpoint}RegistrarParticipacaoLive/${usuarioId}/${aulaId}`,
      {},
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Registra acesso a um menu da plataforma
   * Chamar na primeira vez que o usuário acessar cada menu
   */
  registrarAcessoMenu(usuarioId: number, menuIdentificador: MenuIdentificador): Observable<GamificacaoResponse> {
    return this.http.post<GamificacaoResponse>(
      `${this.controllerEndpoint}RegistrarAcessoMenu/${usuarioId}/${menuIdentificador}`,
      {},
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Obtém pontuação total do usuário
   */
  getPontuacaoTotal(usuarioId: number): Observable<number> {
    return this.http.get<number>(
      `${this.controllerEndpoint}PontuacaoTotal/${usuarioId}`,
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Obtém posição do usuário no ranking
   */
  getPosicaoRanking(usuarioId: number): Observable<number> {
    return this.http.get<number>(
      `${this.controllerEndpoint}PosicaoRanking/${usuarioId}`,
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Obtém histórico de pontuação do usuário
   */
  getHistoricoPontuacao(usuarioId: number, limite: number = 10): Observable<HistoricoPontuacaoResponse[]> {
    return this.http.get<HistoricoPontuacaoResponse[]>(
      `${this.controllerEndpoint}HistoricoPontuacao/${usuarioId}?limite=${limite}`,
      { headers: this.getHeader(true) }
    );
  }
}
